import { useCallback, useLayoutEffect, useRef } from 'react';
import {
  fetchHospitalitySnapshot,
  type HospitalitySnapshot,
} from '../infra/hospitalityRepo';
import { drainKitchenPrintJobs } from '../../printing/application/drainKitchenPrintJobs';

type Options = {
  branchId: string | null | undefined;
  recentWindowMinutes: number;
  /** Aplica el snapshot en estado de pantalla (setTickets/setTables/...). */
  onSnapshot: (snapshot: HospitalitySnapshot) => void;
  /** Inyectable para tests aislados; default = cola durable real. */
  drainPrintJobs?: (branchId: string) => Promise<number>;
};

/**
 * Snapshot de Cocina scoping por sucursal + orden de request.
 * Una respuesta tardía de otra sucursal (o una request vieja de la misma)
 * no pisa el estado vigente ni dispara el drenaje de impresión con ese
 * branch obsoleto. Último request gana.
 */
export function useKitchenTicketSync({
  branchId,
  recentWindowMinutes,
  onSnapshot,
  drainPrintJobs = drainKitchenPrintJobs,
}: Options): () => Promise<HospitalitySnapshot | null> {
  const scopeRef = useRef<{ branchId: string } | null>(null);
  const requestRef = useRef(0);
  const drainInFlightRef = useRef(false);
  const onSnapshotRef = useRef(onSnapshot);
  onSnapshotRef.current = onSnapshot;

  useLayoutEffect(() => {
    const scope = branchId ? { branchId } : null;
    scopeRef.current = scope;
    return () => {
      if (scopeRef.current === scope) scopeRef.current = null;
    };
  }, [branchId]);

  return useCallback(async () => {
    const scope = scopeRef.current;
    if (!scope) return null;
    const request = ++requestRef.current;
    const isCurrent = () => scopeRef.current === scope && requestRef.current === request;
    const snapshot = await fetchHospitalitySnapshot(scope.branchId, recentWindowMinutes);
    if (!isCurrent()) return null;
    onSnapshotRef.current(snapshot);
    if (!drainInFlightRef.current) {
      drainInFlightRef.current = true;
      void drainPrintJobs(scope.branchId)
        .catch(() => undefined)
        .finally(() => {
          drainInFlightRef.current = false;
        });
    }
    return snapshot;
  }, [recentWindowMinutes, drainPrintJobs]);
}
